import styled from '@emotion/styled';
import { Button, colors, SearchBar } from '@innovationdepartment/proxima-ui';
import LinkIcon from '@mui/icons-material/Link';
import type { Table as TableInstance } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useNavigate } from 'react-router';
import {
  ContentSection,
  SectionTitle,
  SortIndicator,
  Table,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow
} from '../BrandTags.styles';

type BrandInfo = {
  brandId: string;
  name: string;
  domain: string;
};

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SearchWrapper = styled.div`
  margin-bottom: 20px;
`;

const BrandUrlLink = styled.a`
  color: ${colors.neutral80};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: ${colors.blue50};
  }

  svg {
    font-size: 16px;
    color: ${colors.neutral60};
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  font-size: 14px;
  color: ${colors.neutral60};
`;

const PaginationButton = styled.button`
  padding: 6px 12px;
  background-color: transparent;
  color: ${colors.neutral80};
  border: 1px solid ${colors.neutral20};
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  font-weight: 500;

  &:hover:not(:disabled) {
    background-color: ${colors.neutral5};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${colors.neutral60};
  font-style: italic;
`;

type BrandsUsingTagViewProps = {
  table: TableInstance<BrandInfo>;
  loading: boolean;
  onSearch: (value: string) => void;
  onRemoveBrand: (brandId: string) => void;
};

const BrandsUsingTagView = ({
  table,
  loading,
  onSearch,
  onRemoveBrand
}: BrandsUsingTagViewProps) => {
  const navigate = useNavigate();
  const totalBrands = table.getFilteredRowModel().rows.length;
  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = table.getPageCount();

  const handleRowClick = (brandId: string) => {
    navigate(`/brand/${brandId}`);
  };

  const handleRemoveClick = (e: React.MouseEvent, brandId: string) => {
    e.stopPropagation();
    onRemoveBrand(brandId);
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <ContentSection>
      <SectionHeader>
        <SectionTitle>BRANDS USING THIS TAG ({totalBrands})</SectionTitle>
      </SectionHeader>

      <SearchWrapper>
        <SearchBar placeholder="Search brands..." onChange={onSearch} />
      </SearchWrapper>

      {!!loading && <EmptyState>Loading brands...</EmptyState>}

      {!loading && totalBrands === 0 && <EmptyState>No brands using this tag</EmptyState>}

      {!loading && totalBrands > 0 && (
        <>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <TableHeaderCell
                      key={header.id}
                      sortable={header.column.getCanSort()}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {{
                        asc: <SortIndicator>↑</SortIndicator>,
                        desc: <SortIndicator>↓</SortIndicator>
                      }[header.column.getIsSorted() as string] ?? null}
                    </TableHeaderCell>
                  ))}
                </tr>
              ))}
            </TableHeader>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <TableRow key={row.id} onClick={() => handleRowClick(row.original.brandId)}>
                  {row.getVisibleCells().map(cell => {
                    // Special rendering for domain column
                    if (cell.column.id === 'domain') {
                      const { domain } = row.original;
                      const url = `https://${domain}`;
                      return (
                        <TableCell key={cell.id}>
                          <BrandUrlLink
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleLinkClick}
                          >
                            {domain}
                            <LinkIcon />
                          </BrandUrlLink>
                        </TableCell>
                      );
                    }

                    // Special rendering for actions column
                    if (cell.column.id === 'actions') {
                      return (
                        <TableCell key={cell.id}>
                          <Button
                            label="Remove"
                            color="error"
                            size="small"
                            onClick={e => {
                              if (e) handleRemoveClick(e, row.original.brandId);
                            }}
                          />
                        </TableCell>
                      );
                    }

                    // Default rendering for other columns
                    return (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </tbody>
          </Table>

          <Pagination>
            <PaginationButton
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              [&lt; Previous]
            </PaginationButton>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <PaginationButton onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              [Next &gt;]
            </PaginationButton>
          </Pagination>
        </>
      )}
    </ContentSection>
  );
};

export default BrandsUsingTagView;
